import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../config/api';
import AddMembersModal from '../components/AddMembersModal';
import AssignBookModal from '../components/AssignBookModal';
import demoData from '../data/demo_institutions.json';
import {
  ArrowLeftIcon,
  UsersIcon,
  BookOpenIcon,
  PlusIcon,
  TrashIcon,
  BookmarkSquareIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  AcademicCapIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

interface Member {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  joined_at: string;
  group_id: string | null;
}

interface BookAssignment {
  id: string;
  group_id: string;
  book_id: string;
  book_title: string | null;
  book_author: string | null;
  book_cover_url: string | null;
  assigned_by: string;
  assigned_at: string;
}

interface GroupInfo {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
}

const InstitutionGroupDetail: React.FC = () => {
  const { id: institutionId, groupId } = useParams<{ id: string; groupId: string }>();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'members' | 'books'>('members');
  const [memberRoleTab, setMemberRoleTab] = useState<'students' | 'teachers'>('students');
  const [group, setGroup] = useState<GroupInfo | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [books, setBooks] = useState<BookAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [showAssignBook, setShowAssignBook] = useState(false);
  // Individual-assignment target — when set, the modal opens in
  // individual mode for this specific student.
  const [individualTarget, setIndividualTarget] = useState<Member | null>(null);
  // Bumped after an individual assignment succeeds (or is revoked) so any
  // expanded student tile refetches its assignments list. Cheap to bump
  // and keeps the StudentTile children stateless about cross-tile updates.
  const [individualRefreshNonce, setIndividualRefreshNonce] = useState(0);

  const myRole = user?.institution_memberships?.find(
    m => m.institution_id === institutionId && ['institution_admin', 'institution_sub_admin'].includes(m.role)
  )?.role;
  const isAdminOrSub = user?.is_admin || !!myRole;
  const canAssignBooks = isAdminOrSub || user?.institution_memberships?.some(
    m => m.institution_id === institutionId && m.group_id === groupId && m.role === 'teacher'
  );

  const isDemo = institutionId?.startsWith('demo-');

  // Split group members into students and teachers so the inner tab strip
  // can switch between them. Anything that isn't a 'teacher' is treated
  // as a student here — admins/sub-admins live at the institution level
  // and never appear in a group's members list.
  const { students, teachers } = useMemo(() => {
    const s: Member[] = [];
    const t: Member[] = [];
    for (const m of members) {
      if (m.role === 'teacher') t.push(m);
      else s.push(m);
    }
    return { students: s, teachers: t };
  }, [members]);

  const fetchData = useCallback(async () => {
    // Demo — load from JSON
    if (isDemo) {
      const demoInst = demoData.institutions.find((i) => i.id === institutionId);
      const demoGroup = demoInst?.groups.find((g) => g.id === groupId);
      if (demoGroup) {
        setGroup({
          id: demoGroup.id,
          name: demoGroup.name,
          description: demoGroup.description,
          is_active: demoGroup.is_active,
        });
        const baseDate = new Date('2026-01-10');
        setMembers(demoGroup.students.map((s, i) => {
          // Spread joined dates over a few weeks with some randomness
          const offsetDays = Math.floor(i * 2.5) + Math.floor(Math.abs(Math.sin(i * 7)) * 5);
          const joinedDate = new Date(baseDate);
          joinedDate.setDate(joinedDate.getDate() + offsetDays);
          return {
            id: `demo-m-${i}`,
            user_id: `demo-u-${i}`,
            email: s.email,
            full_name: s.name,
            role: 'student',
            is_active: true,
            joined_at: joinedDate.toISOString(),
            group_id: demoGroup.id,
          };
        }));
        setBooks([]);
      }
      setLoading(false);
      return;
    }

    try {
      const [groupsRes, membersRes, booksRes] = await Promise.all([
        api.get(`/api/institutions/${institutionId}/groups`),
        api.get(`/api/institutions/${institutionId}/groups/${groupId}/members`),
        api.get(`/api/institutions/${institutionId}/groups/${groupId}/books`),
      ]);
      const groupData = groupsRes.data.find((g: GroupInfo) => g.id === groupId);
      setGroup(groupData || null);
      setMembers(membersRes.data);
      setBooks(booksRes.data);
    } catch (error) {
      console.error('Failed to fetch group data:', error);
    } finally {
      setLoading(false);
    }
  }, [institutionId, groupId, isDemo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRemoveMember = async (userId: string) => {
    if (!window.confirm('Remove this member from the group?')) return;
    try {
      await api.delete(`/api/institutions/${institutionId}/groups/${groupId}/members/${userId}`);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleRemoveFromInstitution = async (userId: string) => {
    if (!window.confirm('Remove this user from the entire institution (all groups)?')) return;
    try {
      await api.delete(`/api/institutions/${institutionId}/members/${userId}`);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to remove from institution');
    }
  };

  const handleRemoveBook = async (bookId: string) => {
    if (!window.confirm('Remove this book from the group?')) return;
    try {
      await api.delete(`/api/institutions/${institutionId}/groups/${groupId}/books/${bookId}`);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to remove book');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(`/institution/${institutionId}`)}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'
            }`}
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {group?.name || 'Group'}
            </h1>
            {group?.description && (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {group.description}
              </p>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className={`flex border-b mb-6 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === 'members'
                ? 'border-cyan-500 text-cyan-500'
                : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            <UsersIcon className="h-4 w-4" />
            Members ({members.length})
          </button>
          <button
            onClick={() => setActiveTab('books')}
            className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === 'books'
                ? 'border-cyan-500 text-cyan-500'
                : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
            }`}
          >
            <BookOpenIcon className="h-4 w-4" />
            Books ({books.length})
          </button>
        </div>

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div>
            {isAdminOrSub && (
              <div className="mb-4">
                <button
                  onClick={() => setShowAddMembers(true)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5"
                  style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Members
                </button>
              </div>
            )}

            {members.length === 0 ? (
              <div className={`text-center py-12 rounded-xl border ${
                isDarkMode ? 'border-slate-700/50 text-gray-400' : 'border-gray-200 text-gray-500'
              }`}>
                <UsersIcon className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>No members in this group yet.</p>
              </div>
            ) : (
              <>
                {/* Inner role-split tabs. Students are tiles (expandable
                    for individual assignments + future per-student stats);
                    teachers stay in a compact table since the per-row
                    actions are simpler. */}
                <div
                  className={`flex gap-1 mb-4 p-1 rounded-lg w-fit ${
                    isDarkMode ? 'bg-slate-900/50' : 'bg-gray-100'
                  }`}
                  role="tablist"
                >
                  <RoleTabButton
                    active={memberRoleTab === 'students'}
                    onClick={() => setMemberRoleTab('students')}
                    isDarkMode={isDarkMode}
                    icon={<UsersIcon className="h-4 w-4" />}
                    label="Students"
                    count={students.length}
                  />
                  <RoleTabButton
                    active={memberRoleTab === 'teachers'}
                    onClick={() => setMemberRoleTab('teachers')}
                    isDarkMode={isDarkMode}
                    icon={<AcademicCapIcon className="h-4 w-4" />}
                    label="Teachers"
                    count={teachers.length}
                  />
                </div>

                {memberRoleTab === 'students' && (
                  students.length === 0 ? (
                    <div className={`text-center py-12 rounded-xl border ${
                      isDarkMode ? 'border-slate-700/50 text-gray-400' : 'border-gray-200 text-gray-500'
                    }`}>
                      <UsersIcon className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p>No students in this group yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {students.map((student) => (
                        <StudentTile
                          key={student.id}
                          student={student}
                          institutionId={institutionId!}
                          isAdminOrSub={!!isAdminOrSub}
                          isDarkMode={isDarkMode}
                          refreshNonce={individualRefreshNonce}
                          onAssignClick={() => setIndividualTarget(student)}
                          onRevoked={() => setIndividualRefreshNonce(n => n + 1)}
                          onRemoveFromGroup={() => handleRemoveMember(student.user_id)}
                          onRemoveFromInstitution={() => handleRemoveFromInstitution(student.user_id)}
                        />
                      ))}
                    </div>
                  )
                )}

                {memberRoleTab === 'teachers' && (
                  teachers.length === 0 ? (
                    <div className={`text-center py-12 rounded-xl border ${
                      isDarkMode ? 'border-slate-700/50 text-gray-400' : 'border-gray-200 text-gray-500'
                    }`}>
                      <AcademicCapIcon className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p>No teachers in this group yet.</p>
                    </div>
                  ) : (
                    <div className={`rounded-xl border overflow-hidden ${
                      isDarkMode ? 'border-slate-700/50' : 'border-gray-200'
                    }`}>
                      <table className="w-full">
                        <thead>
                          <tr className={isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50'}>
                            <th className={`text-left px-4 py-3 text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Name</th>
                            <th className={`text-left px-4 py-3 text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Email</th>
                            <th className={`text-left px-4 py-3 text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Joined</th>
                            {isAdminOrSub && (
                              <th className={`text-right px-4 py-3 text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700/50' : 'divide-gray-200'}`}>
                          {teachers.map((teacher) => (
                            <tr key={teacher.id} className={isDarkMode ? 'bg-slate-800/30' : 'bg-white'}>
                              <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {teacher.full_name || '-'}
                              </td>
                              <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {teacher.email}
                              </td>
                              <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {new Date(teacher.joined_at).toLocaleDateString()}
                              </td>
                              {isAdminOrSub && (
                                <td className="px-4 py-3 text-right">
                                  <div className="flex items-center justify-end gap-3">
                                    <button
                                      onClick={() => handleRemoveMember(teacher.user_id)}
                                      className="text-red-400 hover:text-red-300 text-xs"
                                      title="Remove from group"
                                    >
                                      Remove
                                    </button>
                                    <button
                                      onClick={() => handleRemoveFromInstitution(teacher.user_id)}
                                      className="text-red-400 hover:text-red-300 text-xs"
                                      title="Remove from institution"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
              </>
            )}
          </div>
        )}

        {/* Books Tab */}
        {activeTab === 'books' && (
          <div>
            {canAssignBooks && (
              <div className="mb-4">
                <button
                  onClick={() => setShowAssignBook(true)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5"
                  style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
                >
                  <PlusIcon className="h-4 w-4" />
                  Assign Book
                </button>
              </div>
            )}

            {books.length === 0 ? (
              <div className={`text-center py-12 rounded-xl border ${
                isDarkMode ? 'border-slate-700/50 text-gray-400' : 'border-gray-200 text-gray-500'
              }`}>
                <BookOpenIcon className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>No books assigned to this group yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {books.map((assignment) => (
                  <div
                    key={assignment.id}
                    className={`flex items-center gap-4 p-4 rounded-xl border ${
                      isDarkMode
                        ? 'bg-slate-800/50 border-slate-700/50'
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    {assignment.book_cover_url ? (
                      <img
                        src={assignment.book_cover_url}
                        alt={assignment.book_title || 'Book'}
                        className="h-16 w-12 rounded object-cover"
                      />
                    ) : (
                      <div className={`h-16 w-12 rounded flex items-center justify-center ${
                        isDarkMode ? 'bg-slate-700' : 'bg-gray-100'
                      }`}>
                        <BookOpenIcon className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {assignment.book_title || 'Unknown Book'}
                      </h4>
                      <p className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {assignment.book_author || 'Unknown Author'}
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Added {new Date(assignment.assigned_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        navigate(
                          `/analytics-live/${institutionId}/group/${groupId}/book/${assignment.book_id}`,
                        )
                      }
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        isDarkMode
                          ? 'bg-frost-500/20 text-frost-300 hover:bg-frost-500/30'
                          : 'bg-frost-100 text-frost-700 hover:bg-frost-200'
                      }`}
                      title="View live analytics for this cohort"
                    >
                      <ChartBarIcon className="h-4 w-4" />
                      Analytics
                    </button>
                    {canAssignBooks && (
                      <button
                        onClick={() => handleRemoveBook(assignment.book_id)}
                        className="text-red-400 hover:text-red-300 p-1"
                        title="Remove book"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Modals */}
        {showAddMembers && (
          <AddMembersModal
            institutionId={institutionId!}
            groupId={groupId!}
            onClose={() => setShowAddMembers(false)}
            onAdded={fetchData}
          />
        )}

        {showAssignBook && (
          <AssignBookModal
            institutionId={institutionId!}
            groupId={groupId!}
            onClose={() => setShowAssignBook(false)}
            onAssigned={fetchData}
          />
        )}

        {individualTarget && (
          <AssignBookModal
            institutionId={institutionId!}
            userId={individualTarget.user_id}
            targetLabel={individualTarget.full_name || individualTarget.email}
            onClose={() => setIndividualTarget(null)}
            onAssigned={() => {
              setIndividualTarget(null);
              setIndividualRefreshNonce(n => n + 1);
            }}
          />
        )}
      </div>
    </Layout>
  );
};

// --- Subcomponents ---

interface RoleTabButtonProps {
  active: boolean;
  onClick: () => void;
  isDarkMode: boolean;
  icon: React.ReactNode;
  label: string;
  count: number;
}

const RoleTabButton: React.FC<RoleTabButtonProps> = ({ active, onClick, isDarkMode, icon, label, count }) => {
  const base = 'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors';
  const activeStyles = isDarkMode ? 'bg-slate-700 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm';
  const inactiveStyles = isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700';
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`${base} ${active ? activeStyles : inactiveStyles}`}
    >
      {icon}
      <span>{label}</span>
      <span className={`ml-1 px-1.5 py-0.5 rounded text-xs font-semibold ${
        active
          ? isDarkMode ? 'bg-slate-900/60 text-cyan-300' : 'bg-gray-100 text-cyan-700'
          : isDarkMode ? 'bg-slate-800 text-gray-500' : 'bg-gray-200 text-gray-500'
      }`}>{count}</span>
    </button>
  );
};

interface IndividualAssignment {
  id: string;
  user_id: string;
  book_id: string;
  book_title: string | null;
  book_author: string | null;
  book_cover_url: string | null;
  assigned_by: string;
  assigned_at: string;
}

interface StudentTileProps {
  student: Member;
  institutionId: string;
  isAdminOrSub: boolean;
  isDarkMode: boolean;
  // Bumped at the parent level whenever an individual assignment is
  // created or revoked anywhere on the page. The tile refetches its own
  // assignments list when this changes (and it's currently expanded).
  refreshNonce: number;
  onAssignClick: () => void;
  onRevoked: () => void;
  onRemoveFromGroup: () => void;
  onRemoveFromInstitution: () => void;
}

const StudentTile: React.FC<StudentTileProps> = ({
  student,
  institutionId,
  isAdminOrSub,
  isDarkMode,
  refreshNonce,
  onAssignClick,
  onRevoked,
  onRemoveFromGroup,
  onRemoveFromInstitution,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [assignments, setAssignments] = useState<IndividualAssignment[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    const fetchAssignments = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(
          `/api/institutions/${institutionId}/students/${student.user_id}/books`,
        );
        if (!cancelled) setAssignments(res.data);
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Failed to load assignments');
          setAssignments([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAssignments();
    return () => {
      cancelled = true;
    };
  }, [expanded, refreshNonce, institutionId, student.user_id]);

  const handleRevoke = async (bookId: string, bookTitle: string | null) => {
    if (!window.confirm(`Revoke "${bookTitle || 'this book'}" from ${student.full_name || student.email}?`)) return;
    setRevoking(bookId);
    try {
      await api.delete(
        `/api/institutions/${institutionId}/students/${student.user_id}/books/${bookId}`,
      );
      onRevoked();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to revoke');
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className={`rounded-xl border overflow-hidden ${
      isDarkMode ? 'border-slate-700/50 bg-slate-800/30' : 'border-gray-200 bg-white'
    }`}>
      {/* Header — clickable, toggles expansion */}
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
          isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-gray-50'
        }`}
        aria-expanded={expanded}
      >
        {expanded
          ? <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          : <ChevronRightIcon className={`h-4 w-4 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
        }
        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center">
          <div className="min-w-0">
            <div className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {student.full_name || '—'}
            </div>
            <div className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {student.email}
            </div>
          </div>
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Joined {new Date(student.joined_at).toLocaleDateString()}
          </div>
          <div className="hidden sm:block">
            <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400">
              {student.role}
            </span>
          </div>
        </div>
        {isAdminOrSub && (
          <div
            className="flex items-center gap-2 flex-shrink-0"
            // Stop the parent toggle when interacting with the action
            // buttons; keyboard users can still tab to them and activate
            // with Enter/Space without expanding the tile.
            onClick={(e) => e.stopPropagation()}
          >
            <span
              role="button"
              tabIndex={0}
              onClick={onRemoveFromGroup}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRemoveFromGroup(); } }}
              className="text-red-400 hover:text-red-300 text-xs cursor-pointer select-none"
              title="Remove from group"
            >
              Remove
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={onRemoveFromInstitution}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRemoveFromInstitution(); } }}
              className="text-red-400 hover:text-red-300 text-xs cursor-pointer select-none p-1"
              title="Remove from institution"
            >
              <TrashIcon className="h-4 w-4" />
            </span>
          </div>
        )}
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className={`border-t px-4 py-4 space-y-3 ${isDarkMode ? 'border-slate-700/50' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Individually assigned books
            </h4>
            {isAdminOrSub && (
              <button
                onClick={onAssignClick}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-white inline-flex items-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
              >
                <BookmarkSquareIcon className="h-3.5 w-3.5" />
                Assign book
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center gap-2 py-3">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-500"></div>
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Loading assignments…
              </span>
            </div>
          ) : error ? (
            <div className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{error}</div>
          ) : assignments && assignments.length === 0 ? (
            <div className={`text-xs py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No books assigned individually. Group assignments still apply.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(assignments || []).map((a) => (
                <div
                  key={a.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    isDarkMode ? 'bg-slate-900/40 border-slate-700/50' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  {a.book_cover_url ? (
                    <img src={a.book_cover_url} alt={a.book_title || ''} className="h-12 w-9 rounded object-cover" />
                  ) : (
                    <div className={`h-12 w-9 rounded flex items-center justify-center ${
                      isDarkMode ? 'bg-slate-700' : 'bg-gray-200'
                    }`}>
                      <BookOpenIcon className="h-4 w-4 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {a.book_title || 'Untitled'}
                    </div>
                    <div className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {a.book_author || '—'}
                    </div>
                    <div className={`text-[11px] mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Assigned {new Date(a.assigned_at).toLocaleDateString()}
                    </div>
                  </div>
                  {isAdminOrSub && (
                    <button
                      onClick={() => handleRevoke(a.book_id, a.book_title)}
                      disabled={revoking === a.book_id}
                      className="text-red-400 hover:text-red-300 p-1 disabled:opacity-50"
                      title="Revoke this assignment"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InstitutionGroupDetail;
